'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ContextNavigation, ListPageSearch } from '@terra/ui-design-system';
import { FINDINGS_GROUP_PUBLISHED_KEY } from '@terra/shared-contracts-findings';
import type { FindingsBulkFilter } from '../../../clients/testerClient';
import { useFindingsGroupCounts } from '../../../hooks/api/useFindingsGroupCounts';
import { ListPageRefreshButton } from '../../common/ListPageRefreshButton';
import {
  FINDINGS_PUBLICATION_CHIP,
  omitFindingsPublicationFilter,
  type FindingsPublicationChip,
} from '../findings-table/findings-list.model';

type FindingsContextNavigationProps = {
  targetId: string | null | undefined;
  filter: FindingsBulkFilter;
  publicationChip: FindingsPublicationChip;
  onPublicationChipChange: (chip: FindingsPublicationChip) => void;
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
  optionsMenu: ReactNode;
};

export function FindingsContextNavigation({
  targetId,
  filter,
  publicationChip,
  onPublicationChipChange,
  searchTerm,
  onSearchTermChange,
  onRefresh,
  isLoading,
  optionsMenu,
}: FindingsContextNavigationProps) {
  const t = useTranslations('proxy.findings.chips');
  const tSearch = useTranslations('designSystem.common');
  const tFindingEntity = useTranslations('designSystem.findings.findingDrawer');
  const countsFilter = useMemo(
    () => omitFindingsPublicationFilter(filter),
    [filter],
  );
  const { countByKey } = useFindingsGroupCounts({
    targetId,
    filter: countsFilter,
    groupBy: 'isPublished',
  });
  const items = useMemo(() => {
    const publishedCount = countByKey?.get(
      FINDINGS_GROUP_PUBLISHED_KEY.PUBLISHED,
    );
    const unpublishedCount = countByKey?.get(
      FINDINGS_GROUP_PUBLISHED_KEY.UNPUBLISHED,
    );

    return [
      {
        value: FINDINGS_PUBLICATION_CHIP.ALL,
        label: t('all'),
        count: countByKey
          ? (publishedCount ?? 0) + (unpublishedCount ?? 0)
          : undefined,
      },
      {
        value: FINDINGS_PUBLICATION_CHIP.PUBLISHED,
        label: t('published'),
        count: countByKey ? (publishedCount ?? 0) : undefined,
      },
      {
        value: FINDINGS_PUBLICATION_CHIP.UNPUBLISHED,
        label: t('unpublished'),
        count: countByKey ? (unpublishedCount ?? 0) : undefined,
      },
    ];
  }, [countByKey, t]);

  return (
    <ContextNavigation
      ariaLabel={t('label')}
      items={items}
      value={publicationChip}
      onValueChange={onPublicationChipChange}
      actions={
        <>
          <ListPageSearch
            value={searchTerm}
            onValueChange={onSearchTermChange}
            placeholder={tSearch('searchByNameOrId', {
              entityName: tFindingEntity('entityTitle'),
            })}
          />
          <ListPageRefreshButton onRefresh={onRefresh} isLoading={isLoading} />
          {optionsMenu}
        </>
      }
    />
  );
}
